import React, { useEffect, useState } from 'react';

import AlertDialog from '../../shared/components/AlertDialog';
import AuthService from '../../shared/services/AuthService';
import CourseGroupCard from './CourseGroupCard';
import Grid from '@mui/material/Unstable_Grid2';
import Overlay from '../../shared/components/Overlay';
import PayPalService from '../../shared/services/PayPalService';
import PaymentDialog from '../../shared/components/PaymentDialog';
import PaymentEntry from '../../dashboard/models/PaymentEntry';
import { PaymentUIResult } from '../../shared/components/PaymentButton';
import { SUPPORT_MESSAGE } from '../../shared/constants';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import { Typography } from '@mui/material';
import UserGroupPayment from '../../dashboard/models/UserGroupPayment';
import UserGroupVm from '../models/UserGroupVm';
import usePaymentHistory from '../../dashboard/hooks/usePaymentHistory';
import usePaymentSettings from '../../shared/hooks/usePaymentSettings';
import usePublicGroups from '../../group/hooks/usePublicGroups';

interface CourseCatalogViewProps {
  groups: UserGroupVm[];
}

const CourseCatalogView = (props: CourseCatalogViewProps) => {
  const userkey = AuthService.Userkey;
  const {
    joinGroup,
    joining,
    joinError,
    joinGroupByCode,
    joiningCode,
    joinCodeError
  } = usePublicGroups();

  const [joinFailed, setJoinFailed] = useState(false);
  useEffect(() => {
    if (joinError || joinCodeError) {
      setJoinFailed(true);
    }
  }, [joinError, joinCodeError]);

  const { loading, error, paymentSettings } = usePaymentSettings();
  const [payment, setPayment] = useState<{
    groupkey: number;
    groupName: string;
    price: number;
    currency: string;
  }>(null);
  const [currency, setCurrency] = useState<string>();
  const [userGroupPaymentHistory, setUserGroupPaymentHistory] =
    useState<UserGroupPayment>(null);

  const { savePaymentHistory, saving, saveError, paymentHistory } =
    usePaymentHistory();

  useEffect(() => {
    setCurrency(paymentSettings?.currency);
  }, [paymentSettings?.currency]);

  useEffect(() => {
    if (saving) {
      setUserGroupPaymentHistory(null);
    }
  }, [saving]);

  useEffect(() => {
    if (saveError) {
      setUserGroupPaymentHistory(null);
    }
  }, [saveError]);

  useEffect(() => {
    if (userGroupPaymentHistory === null) {
      setUserGroupPaymentHistory(paymentHistory);
    }
  }, [paymentHistory]);

  useEffect(() => {
    if (payment && userGroupPaymentHistory?.status === 'COMPLETED') {
      joinGroup(userkey, userGroupPaymentHistory.groupkey);
      setPayment(null);
    }
  }, [userGroupPaymentHistory]);

  function handleJoin(
    groupkey: number,
    groupName: string,
    price: number,
    purchased: boolean
  ) {
    if (price > 0 && purchased === false) {
      setPayment({ groupkey, groupName, price, currency });
    } else {
      joinGroup(userkey, groupkey);
    }
  }

  function handlePaymentComplete(result: PaymentUIResult, details: any) {
    if (result !== 'error' && result !== 'cancel') {
      const input = {
        reference: JSON.stringify(details),
        description: `Group: ${payment?.groupName} (${
          payment?.groupkey
        }), Price: ${PayPalService.formatPrice(
          payment?.price,
          payment?.currency
        )}`,
        amount: payment.price,
        currency,
        status: details?.status
      } as PaymentEntry;
      savePaymentHistory(payment.groupkey, input);
    }

    if (result !== 'success') {
      setPayment(null);
    }
  }

  const handleJoinByCode = (groupkey: number, code: string) => {
    joinGroupByCode(userkey, groupkey, code);
  };

  const items: { title: string; key: string | number; group?: UserGroupVm }[] =
    [
      ...props.groups.map((g) => ({
        title: g.title,
        key: g.groupkey,
        group: g
      }))
    ].sort((a, b) => a.title.localeCompare(b.title));

  return (
    <>
      <div className="course-catalog-body">
        <Grid container spacing={2}>
          {items?.length > 0 ? (
            items.map((item) => (
              <Grid key={item.key} xs={12}>
                {item.group && (
                  <CourseGroupCard
                    group={item.group}
                    currency={currency}
                    onJoin={handleJoin}
                    onJoinByCode={handleJoinByCode}
                  />
                )}
              </Grid>
            ))
          ) : (
            <Grid>
              <Grid container alignItems="center" spacing={1}>
                <Grid>
                  <SentimentVeryDissatisfiedIcon
                    style={{ verticalAlign: 'middle' }}
                  />
                </Grid>
                <Grid>
                  <Typography variant="h6">
                    There are no courses available.
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          )}
        </Grid>
        {payment !== null && (
          <PaymentDialog
            open={payment !== null}
            title="Checkout"
            message={`Hit the PayPal button below to purchase ${
              payment?.groupName
            } for ${PayPalService.formatPrice(
              payment?.price,
              payment?.currency
            )}.`}
            item={payment?.groupName}
            price={payment?.price}
            currency={currency}
            onClose={handlePaymentComplete}
          />
        )}
      </div>
      {(joining || joiningCode) && <Overlay spinner />}
      {joinFailed && (
        <AlertDialog
          title="Joining a course has failed."
          message={SUPPORT_MESSAGE}
          onClose={() => setJoinFailed(false)}
        />
      )}
    </>
  );
};

export default CourseCatalogView;
